import { ChangeDetectionStrategy, Component } from '@angular/core';

import { TabBarComponent } from './tab-bar.component';
import { money } from './money';
import { currentLocale } from './locale';

/**
 * Money: "What am I worth today?"
 *
 * Every string is translated: the app ships in the same five languages this
 * site does, so a drawn screen has to speak the reader's language too.
 *
 * Structure taken from a 0.3.0 screenshot:
 *
 * - The band has a subline and no hairline. Only Today draws the arithmetic.
 * - The ground opens with a rule, then each group of accounts has a header
 *   carrying the group's own total on the right.
 * - An account row is a disc, a name, the balance and a chevron. No subtitle:
 *   the app does not repeat the account type under the name.
 *
 * One deliberate difference from the screenshot: the shipped app draws every
 * account disc in the same neutral grey. Here each account has its own hue and
 * its own glyph, so four accounts read as four things at a glance rather than
 * as a column of identical circles. The hues come from the category palette,
 * which is the one sanctioned exception to the low-chroma rule, and the glyph
 * stays white as it does on a category mark.
 *
 * The two glyphs in the kicker row are transfer and add account. Transfers
 * matter more than they look: in the data a transfer is a parent row plus two
 * legs, which is why moving money between your own accounts never registers
 * as income or as spending.
 *
 * The groups sum to the figure on the band: 4,500 + 9,100 − 760 = 12,840.
 */
@Component({
  selector: 'app-screen-money',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabBarComponent],
  template: `
    <div class="nq-screen">
      <div class="band">
        <div class="kicker-row">
          <span class="kicker">{{ t.title }}</span>
          <span class="glyphs">
            <span class="glyphs__swap">⇄</span>
            <span class="glyphs__plus">＋</span>
          </span>
        </div>

        <div class="hero">
          <p class="hero__label">{{ t.label }}</p>
          <p class="hero__figure">{{ netWorth }}</p>
          <p class="hero__subline">{{ t.subline }}</p>
        </div>
      </div>

      <div class="ground">
        <div class="ground-rule"></div>

        @for (group of groups; track group.title) {
        <div class="section-row">
          <span class="section">{{ group.title }}</span>
          <span class="section-value section-total">{{ group.total }}</span>
        </div>

        @for (account of group.accounts; track account.name) {
        <div class="row">
          <span
            class="mark mark--account"
            [style.background]="account.hue"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path [attr.d]="account.glyph" />
            </svg>
          </span>
          <span class="row__title">{{ account.name }}</span>
          <span class="row__amount">{{ account.balance }}</span>
          <span class="chev">›</span>
        </div>
        } }
      </div>

      <app-tab-bar active="money" />
    </div>
  `,
  styles: [
    `
      .nq-screen .glyphs {
        display: inline-flex;
        align-items: center;
        gap: 16.8px;
        color: var(--color-accent-200);
        line-height: 1;
      }

      .nq-screen .glyphs__swap {
        font-size: 15px;
      }

      .nq-screen .glyphs__plus {
        font-size: 18px;
      }

      .nq-screen .row .row__title {
        flex: 1;
      }

      .nq-screen .mark--account svg {
        width: 15px;
        height: 15px;
        stroke: #fff;
        stroke-width: 1.6;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    `,
  ],
})
export class ScreenMoneyComponent {
  private readonly locale = currentLocale();

  protected readonly t = {
    title: $localize`:@@tabMoney:Money`,
    label: $localize`:@@frameMoneyLabel:What you're worth today`,
    subline: $localize`:@@frameMoneySubline:This is your first month on Ledgerly.`,
  };

  /** 4,500 + 9,100 − 760. Change an account, change this. */
  protected readonly netWorth = money(12840, this.locale);

  protected readonly groups = [
    {
      title: $localize`:@@frameGroupSpending:Spending`,
      total: money(4500, this.locale),
      accounts: [
        {
          name: $localize`:@@frameAcctChecking:Checking`,
          balance: money(4120, this.locale),
          hue: '#6e9bf0',
          // A bank front: columns under a pediment.
          glyph: 'M4 10h16M5 10 12 5l7 5M7 10v7M11 10v7M15 10v7M4 19h16',
        },
        {
          name: $localize`:@@frameAcctCash:Cash`,
          balance: money(380, this.locale),
          hue: '#d9bc5e',
          // A note with a coin in the middle.
          glyph: 'M3 7h18v10H3zM12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4',
        },
      ],
    },
    {
      title: $localize`:@@frameGroupSaving:Saving`,
      total: money(9100, this.locale),
      accounts: [
        {
          name: $localize`:@@frameAcctSavings:Savings`,
          balance: money(9100, this.locale),
          hue: '#4fb8a6',
          // Money going in rather than out.
          glyph: 'M12 18V6M7 11l5-5 5 5',
        },
      ],
    },
    {
      title: $localize`:@@frameGroupOwed:Owed`,
      total: money(-760, this.locale),
      accounts: [
        {
          name: $localize`:@@frameAcctCard:Card`,
          balance: money(-760, this.locale),
          hue: '#e8709b',
          // A card with its magnetic stripe.
          glyph: 'M3 6h18v12H3zM3 10h18M6 14h4',
        },
      ],
    },
  ];
}
